import { createGtm, useGtm } from '@gtm-support/vue-gtm'

/**
 * Google Tag Manager, as a client plugin using @gtm-support/vue-gtm. Disabled in dev; runs on built
 * (staging/production) servers. Consent is handled up front by the Consent Mode defaults + Cookiebot
 * (config/consent.ts), so GTM starts in a denied state until the visitor consents.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()

  /*
   * Local dataLayer debugging: set `NUXT_PUBLIC_GTM_DEV=true` in your `nuxt/.env` and restart the dev
   * server. This switches the integration on so events build `window.dataLayer` up in the browser and
   * each dispatch is logged, while `loadScript` stays off, so the container is never fetched and nothing
   * reaches Google. Tracking gates on `enabled` alone, never on `loadScript`, which is what makes the
   * combination safe.
   */
  const isGtmDevMode = import.meta.dev && useRuntimeConfig().public.gtmDev

  const head = injectHead()

  /**
   * Resolves with the document title once Unhead has written it to the DOM.
   *
   * Nuxt suspends head updates for the duration of a navigation: its head plugin sets `pauseDOMUpdates`
   * on `page:start`, suppresses `dom:beforeRender` while that flag is set, and calls `syncHead()` on
   * `page:finish`. The page view below is tracked from `router.afterEach`, which runs before all of that,
   * so reading the title there reports the page the visitor just left. `page:finish` is not late enough
   * either, because Unhead's DOM renderer is debounced through a `setTimeout`, leaving `syncHead()` to
   * schedule the write rather than perform it. `dom:rendered` is the moment the title is in the document.
   *
   * Do not read `document.title` on a timer instead: a fixed delay reports the right title on a fast
   * navigation and the previous page's on a slow one, which looks correct in testing and is wrong in
   * production.
   *
   * The timeout is a safety net for a navigation that renders no head at all, and for Unhead dropping
   * `dom:rendered`, which it marks as deprecated. It resolves with whatever the title is by then rather
   * than rejecting: a wrong title costs less than a page view that never arrives.
   */
  const titleAfterHeadFlush = (timeoutMs = 2000): Promise<string> =>
    new Promise((resolve) => {
      const settle = () => {
        clearTimeout(timer)
        unhook?.()
        resolve(document.title)
      }

      const timer = setTimeout(settle, timeoutMs)
      const unhook = head?.hooks?.hook('dom:rendered', settle)

      if (!unhook) {
        settle()
      }
    })

  nuxtApp.vueApp.use(
    createGtm({
      id: 'GTM-MKJTRLP',
      enabled: !import.meta.dev || isGtmDevMode,
      loadScript: !import.meta.dev,
      debug: isGtmDevMode,
      defer: true,
    }),
  )

  // `createGtm` returns the Vue plugin, not the tracker; the tracker is only reachable once it is
  // installed. `useGtm` reads a module-level instance, so it works here outside a component.
  const gtm = useGtm()

  /*
   * Page views are pushed by hand instead of through vue-gtm's own `vueRouter` integration. That
   * integration sends `content-name` and `content-view-name`, while the GTM container's triggers and
   * variables are built on the payload the Nuxt 2 `@nuxtjs/gtm` module sent. Configuring only the event
   * name to match would put the right event on the dataLayer carrying none of the fields the container
   * reads, so the keys below are kept exactly as that module produced them.
   */
  router.afterEach((to, _from, failure) => {
    // `afterEach` also runs for a navigation that was aborted, cancelled, duplicated or redirected,
    // and counting those produces a page view for a page the visitor never reached.
    if (failure) {
      return
    }

    // vue-router discards whatever an `afterEach` guard returns, so the push waits on the title here
    // rather than the guard awaiting it.
    void titleAfterHeadFlush().then((pageTitle) => {
      gtm?.push({
        routeName: to.name,
        pageType: 'PageView',
        pageUrl: to.fullPath,
        pageTitle,
        event: 'nuxtRoute',
      })
    })
  })
})
